import React, { useEffect, useMemo, useState } from 'react';
import { Download } from '@saleshandy/icons';

import Modal from '../../../../../../../shared/design-system/components/atoms/modal/modal';
import Select from '../../../../../../../shared/design-system/components/select';
import FileSelectorInput from '../../../../do-not-contact/components/modals/import-emails-domains-via-csv/components/file-selector-input';

import { ImportPhoneNumbersViaCsvPayload } from '../../../../../types/do-not-call-list';
import { constants } from '../../../../../../../shared/enums/constants';

type FileData = {
  file: File;
  headers: string[];
} | null;

type HeaderOption = {
  key: string;
  header: string;
};

type IProps = {
  show: boolean;
  isLoading: boolean;
  dncListId: string;
  onCancel: VoidFunction;
  onSubmit: (payload: ImportPhoneNumbersViaCsvPayload) => void;
};

const ImportViaCsv: React.FC<IProps> = ({
  show,
  isLoading,
  dncListId,
  onCancel,
  onSubmit,
}) => {
  const [fileData, setFileData] = useState<FileData>(null);
  const [selectedPhoneNumberHeader, setSelectedPhoneNumberHeader] = useState<HeaderOption>(null);
  const [selectedCountryCodeHeader, setSelectedCountryCodeHeader] = useState<HeaderOption>(null);

  const handleCancel = () => {
    onCancel();
    setFileData(null);
    setSelectedPhoneNumberHeader(null);
    setSelectedCountryCodeHeader(null);
  };

  const handleSubmit = () => {
    onSubmit({
      dncListId,
      file: fileData.file,
      phoneNumberMapping: selectedPhoneNumberHeader.header,
      countryCodeMapping: selectedCountryCodeHeader.header,
    });
  };

  const getDefaultHeader = (
    headers: HeaderOption[],
    pattern: RegExp,
  ): HeaderOption | null => {
    if (headers.length === 1) return headers[0];
    return headers.find(({ header }) => pattern.test(header)) || null;
  };

  const headerOptions = useMemo((): HeaderOption[] => {
    if (!fileData) return [];
    return fileData.headers.map((header, index) => ({
      key: index.toString(),
      header,
    }));
  }, [fileData]);

  useEffect(() => {
    if (fileData) {
      const options = fileData.headers.map((header, index) => ({
        key: index.toString(),
        header,
      }));
      setSelectedPhoneNumberHeader(
        getDefaultHeader(options, /phone/i) || null,
      );
      setSelectedCountryCodeHeader(
        getDefaultHeader(options, /country.*code/i) || null,
      );
    } else {
      setSelectedPhoneNumberHeader(null);
      setSelectedCountryCodeHeader(null);
    }
  }, [fileData]);

  useEffect(() => {
    if (!show) {
      setFileData(null);
      setSelectedPhoneNumberHeader(null);
      setSelectedCountryCodeHeader(null);
    }
  }, [show]);

  const isSubmitDisabled =
    !fileData ||
    !selectedPhoneNumberHeader ||
    !selectedCountryCodeHeader ||
    isLoading;

  const renderSelectDropdown = (
    label: string,
    selectedOption: HeaderOption,
    onChangeOption: (option: HeaderOption) => void,
  ) => (
    <div className="import-column-mapping">
      <h3>{label}</h3>
      <Select<HeaderOption>
        selectedOptionKey={selectedOption?.key}
        options={headerOptions}
        selectedOptionRenderer={([option]) => (
          <span className="semibold-10">{option?.header}</span>
        )}
        optionRenderer={(option) => (
          <span className="semibold-10">{option?.header}</span>
        )}
        onChange={([selected]) => {
          onChangeOption(selected);
        }}
        showSearch
        optionFilterProp="header"
        filterOption={(value, option) =>
          option?.header?.toLowerCase().includes(value.toLowerCase())
        }
        placeholder="Select field"
        disabled={!fileData || headerOptions.length === 0}
      />
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={handleCancel}
      onSubmit={handleSubmit}
      titleContent="Upload CSV"
      showCloseIcon
      submitButtonText="Save"
      isSubmitLoading={isLoading}
      isSubmitDisabled={isSubmitDisabled}
      className="dnc-upload-csv-modal"
      backdrop="static"
      extraModalProps={{ enforceFocus: false }}
      footerContent={
        <div>
          <a
            className="sample-file-btn"
            href={constants.DOWNLOAD_CALL_DNC_SAMPLE_FILE_URL}
            download
          >
            <div className="icon-wrapper">
              <Download />
            </div>
            <p>Try Sample File</p>
          </a>
        </div>
      }
    >
      <>
        <FileSelectorInput handleCSVImport={setFileData} />

        <p className="import-help-text">
          Only the first 10,000 valid items from the selected columns will be
          processed and Max file size allowed is 5 MB
        </p>

        {renderSelectDropdown(
          'Map Country Code Column',
          selectedCountryCodeHeader,
          setSelectedCountryCodeHeader,
        )}

        {renderSelectDropdown(
          'Map Phone Number Column',
          selectedPhoneNumberHeader,
          setSelectedPhoneNumberHeader,
        )}

        <div className="helper-alert-warning-wrapper">
          <p>
            All prospects whose numbers match this list will be marked as Do Not
            Call. You won&apos;t be able to call them anywhere, and call-related
            sequence tasks will not be executed.
          </p>
        </div>
      </>
    </Modal>
  );
};

export default ImportViaCsv;
